import { store } from "@/store/store";
import { VuexModule, Mutation, getModule, Module, Action } from "vuex-module-decorators";
import { ssmHttpService } from "@/shared/services/http-service";
import { API } from "@/shared/api";
import { ChatConversacionDto } from "@/shared/dtos/chat/chat-conversacion.dto";
import { ChatMensajeDto } from "@/shared/dtos/chat/chat-mensaje.dto";
import { ChatMcpConfigDto } from "@/shared/dtos/chat/chat-mcp-config.dto";
import { ChatIaConfigDto } from "@/shared/dtos/chat/chat-ia-config.dto";
import { ChatResponseDto } from "@/shared/dtos/chat/chat-response.dto";
import { ChatPromptDto } from "@/shared/dtos/chat/chat-prompt.dto";
import { ChatMemoriaDto } from "@/shared/dtos/chat/chat-memoria.dto";

@Module({ namespaced: true, name: "chatModule", store, dynamic: true })
class chatModule extends VuexModule {
    public conversaciones: ChatConversacionDto[] = [];
    public conversacionActualId: number = 0;
    public mensajes: ChatMensajeDto[] = [];
    public mcps: ChatMcpConfigDto[] = [];
    public prompts: ChatPromptDto[] = [];
    public departamentos: ChatPromptDto[] = [];
    public memorias: ChatMemoriaDto[] = [];
    public config: ChatIaConfigDto = new ChatIaConfigDto();
    public enviando: boolean = false;
    public ultimaRespuesta: ChatResponseDto = new ChatResponseDto();
    public proveedor: string = "nvidia";

    @Action({ commit: "onGetConversaciones" })
    public async getConversaciones() {
        return await ssmHttpService.get(API.chatIA + "conversaciones");
    }

    @Action({ commit: "onGetMensajes" })
    public async getMensajes(id: number) {
        return await ssmHttpService.get(API.chatIA + "conversacion/" + id);
    }

    @Action({ commit: "onGetMcps" })
    public async getMcps() {
        return await ssmHttpService.get(API.chatIA + "mcps");
    }

    @Action({ commit: "onGetConfig" })
    public async getConfig() {
        try {
            return await ssmHttpService.get(API.chatIA + "config");
        } catch (e) {
            return null; // 404 = sin config todavía; onGetConfig(null) crea un DTO vacío
        }
    }

    @Action({ commit: "onEnviarMensaje" })
    public async enviarMensaje(payload: { conversacionId: number; mensaje: string }) {
        this.context.commit("setEnviando", true);
        try {
            const body = {
                ConversacionId: payload.conversacionId > 0 ? payload.conversacionId : null,
                Mensaje: payload.mensaje
            };
            return await ssmHttpService.post(API.chatIA + "chat", body, false);
        } finally {
            this.context.commit("setEnviando", false);
        }
    }

    @Action
    public async eliminarConversacion(id: number) {
        await ssmHttpService.delete(API.chatIA + "conversacion/" + id, null);
        this.context.commit("removeConversacion", id);
    }

    @Action
    public async toggleMcp(nombre: string) {
        await ssmHttpService.put(API.chatIA + "mcps/" + nombre, {});
        await this.getMcps();
    }

    @Action({ commit: "onGetPrompts" })
    public async getPrompts() {
        return await ssmHttpService.get(API.chatPrompts);
    }

    @Action
    public async inicializarPrompts() {
        await ssmHttpService.post(API.chatPrompts + "inicializar", {});
        await this.getPrompts();
    }

    @Action
    public async togglePrompt(id: number) {
        await ssmHttpService.put(API.chatPrompts + id + "/toggle", {});
        await this.getPrompts();
    }

    @Action
    public async guardarPrompt(item: ChatPromptDto) {
        if (item.id > 0) {
            await ssmHttpService.put(API.chatPrompts, item.toJson());
        } else {
            await ssmHttpService.post(API.chatPrompts, item.toJson());
        }
        await this.getPrompts();
    }

    @Action
    public async eliminarPrompt(id: number) {
        await ssmHttpService.delete(API.chatPrompts + id, null);
        await this.getPrompts();
    }

    @Action({ commit: "onGetDepartamentos" })
    public async getDepartamentos() {
        return await ssmHttpService.get(API.chatPrompts + "departamentos");
    }

    @Action
    public async eliminarDepartamento(id: number) {
        await ssmHttpService.delete(API.chatPrompts + id, null);
        await this.getDepartamentos();
    }

    @Action({ commit: "onGetMemorias" })
    public async getMemorias(tipo?: string) {
        const url = tipo ? API.chatMemoria + "?tipo=" + tipo : API.chatMemoria;
        return await ssmHttpService.get(url);
    }

    @Action
    public async eliminarMemoria(id: number) {
        await ssmHttpService.delete(API.chatMemoria + id, null);
        await this.getMemorias();
    }

    @Action
    public async vaciarMemorias() {
        await ssmHttpService.delete(API.chatMemoria + "vaciar", null);
        this.context.commit("onGetMemorias", []);
    }

    @Action({ commit: "onGetConfig" })
    public async guardarConfig(config: ChatIaConfigDto) {
        return await ssmHttpService.post(API.chatIA + "guardarConfig", config.toJson());
    }

    @Action({ commit: "onEnviarMensaje" })
    public async enviarMensajeXiaoZhi(payload: { conversacionId: number; mensaje: string }) {
        this.context.commit("setEnviando", true);
        try {
            const body = {
                ConversacionId: payload.conversacionId > 0 ? payload.conversacionId : null,
                Mensaje: payload.mensaje
            };
            return await ssmHttpService.post(API.xiaoZhi + "chat", body, false);
        } finally {
            this.context.commit("setEnviando", false);
        }
    }

    @Mutation
    public onGetConversaciones(res: any[]) {
        this.conversaciones = res ? res.map((x: any) => new ChatConversacionDto(x)) : [];
    }

    @Mutation
    public onGetMensajes(res: any[]) {
        this.mensajes = res ? res.map((x: any) => new ChatMensajeDto(x)) : [];
    }

    @Mutation
    public onGetMcps(res: any[]) {
        this.mcps = res ? res.map((x: any) => new ChatMcpConfigDto(x)) : [];
    }

    @Mutation
    public onGetPrompts(res: any[]) {
        this.prompts = res ? res.map((x: any) => new ChatPromptDto(x)) : [];
    }

    @Mutation
    public onGetDepartamentos(res: any[]) {
        this.departamentos = res ? res.map((x: any) => new ChatPromptDto(x)) : [];
    }

    @Mutation
    public onGetMemorias(res: any[]) {
        this.memorias = res ? res.map((x: any) => new ChatMemoriaDto(x)) : [];
    }

    @Mutation
    public onGetConfig(res: any) {
        if (Array.isArray(res)) {
            this.config = res.length > 0 ? new ChatIaConfigDto(res[res.length - 1]) : new ChatIaConfigDto();
        } else {
            this.config = res ? new ChatIaConfigDto(res) : new ChatIaConfigDto();
        }
    }

    @Mutation
    public onEnviarMensaje(res: any) {
        this.ultimaRespuesta = res ? new ChatResponseDto(res) : new ChatResponseDto();
        if (res && res.conversacionId) {
            this.conversacionActualId = res.conversacionId || (res.ConversacionId || 0);
        }
    }

    @Mutation
    public setEnviando(val: boolean) {
        this.enviando = val;
    }

    @Mutation
    public removeConversacion(id: number) {
        this.conversaciones = this.conversaciones.filter((c: ChatConversacionDto) => c.id !== id);
        if (this.conversacionActualId === id) {
            this.conversacionActualId = 0;
            this.mensajes = [];
        }
    }

    @Mutation
    public setProveedor(p: string) {
        this.proveedor = p;
    }

    @Mutation
    public setConversacionActual(id: number) {
        this.conversacionActualId = id;
    }

    @Mutation
    public nuevaConversacion() {
        this.conversacionActualId = 0;
        this.mensajes = [];
    }

    @Mutation
    public addMensajeLocal(msg: ChatMensajeDto) {
        this.mensajes = [...this.mensajes, msg];
    }
}

export default getModule(chatModule);
